"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { BrandedHeader } from "@/components/branded";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { cn } from "@/utilities/core/cn";
import { activeCompetitionSubPage, type CompetitionSubPage } from "./activeCompetitionSubPage";
interface CompetitionBrandedHeaderProps {
  name: string;
  /** Native FansUnited competition ID (e.g. "fb:c:30") — used for logo overrides. */
  competitionId?: string;
  logoUrl?: string;
  /** Whether the competition has a knockout phase (show Draw tab). */
  hasKnockoutPhase?: boolean;
  /** Header gradient colors from section styles */
  colors?: {
    gradientFrom: string;
    gradientTo: string;
    textColor: string;
  };
  /** Background image URL from section styles */
  backgroundImageUrl?: string;
  /**
   * Optional breadcrumb element rendered above the title row inside BrandedHeader.
   * Pass an awaited `<Breadcrumbs ... />` from a server component.
   */
  breadcrumbs?: React.ReactNode;
}

/**
 * CompetitionBrandedHeader — Branded header for the competition detail page on news-blog sites.
 *
 * Renders the competition logo and name via BrandedHeader, followed by a sticky
 * link-based navigation bar (Overview · News · Standings · Matches · Teams).
 *
 * Competition data is passed as props from the server component — no SDK context needed.
 * Must be rendered inside a NavigationProvider.
 */
export function CompetitionBrandedHeader(t0) {
  const $ = _c(30);
  const {
    name,
    competitionId,
    logoUrl,
    hasKnockoutPhase,
    colors,
    backgroundImageUrl,
    breadcrumbs
  } = t0;
  const nav = useNavigation();
  const labels = useLabels();
  const pathname = usePathname();
  const resolvedLogoUrl = logoUrl;
  let navLinks;
  let t1;
  if ($[0] !== hasKnockoutPhase || $[1] !== labels.competitions || $[2] !== labels.matches.title || $[3] !== labels.teamPage.tabs.news || $[4] !== labels.teamPage.tabs.overview || $[5] !== labels.teams.title || $[6] !== nav || $[7] !== pathname) {
    navLinks = [{
      key: "overview",
      href: nav.footballCompetition(),
      label: labels.teamPage.tabs.overview
    }, {
      key: "news",
      href: nav.competitionNews(),
      label: labels.teamPage.tabs.news
    }, {
      key: "videos",
      href: nav.competitionVideos(),
      label: (labels.competitions as any).videos || "Videos"
    }, {
      key: "standings",
      href: nav.standings(),
      label: labels.competitions.standings
    }, {
      key: "matches",
      href: nav.matchesList(),
      label: labels.matches.title
    }, {
      key: "teams",
      href: nav.teamsList(),
      label: labels.teams.title
    }, ...(hasKnockoutPhase ? [{
      key: "draw" as const,
      href: nav.competitionDraw(),
      label: labels.competitions.draw
    }] : [])];
    t1 = activeCompetitionSubPage(pathname, navLinks);
    $[0] = hasKnockoutPhase;
    $[1] = labels.competitions;
    $[2] = labels.matches.title;
    $[3] = labels.teamPage.tabs.news;
    $[4] = labels.teamPage.tabs.overview;
    $[5] = labels.teams.title;
    $[6] = nav;
    $[7] = pathname;
    $[8] = navLinks;
    $[9] = t1;
  } else {
    navLinks = $[8];
    t1 = $[9];
  }
  const active = t1;
  let t2;
  if ($[10] !== active || $[11] !== labels.competitions.pageHeadings || $[12] !== name) {
    t2 = active === "overview" ? name : labels.competitions.pageHeadings[active].replace("{competitionName}", () => name);
    $[10] = active;
    $[11] = labels.competitions.pageHeadings;
    $[12] = name;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const title = t2;
  let t3;
  if ($[14] !== competitionId || $[15] !== resolvedLogoUrl) {
    t3 = <TeamLogo src={resolvedLogoUrl} kind="competition" competitionId={competitionId} size="xl2" />;
    $[14] = competitionId;
    $[15] = resolvedLogoUrl;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== backgroundImageUrl || $[18] !== breadcrumbs || $[19] !== colors || $[20] !== resolvedLogoUrl || $[21] !== t3 || $[22] !== title) {
    t4 = <BrandedHeader titleAs="h1" title={title} imageUrl={resolvedLogoUrl} image={t3} colors={colors} backgroundImageUrl={backgroundImageUrl} breadcrumbs={breadcrumbs} />;
    $[17] = backgroundImageUrl;
    $[18] = breadcrumbs;
    $[19] = colors;
    $[20] = resolvedLogoUrl;
    $[21] = t3;
    $[22] = title;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== navLinks || $[25] !== pathname) {
    t5 = <nav className="py-0.5 tab-bar-surface sticky top-below-nav z-10"><div className="max-w-350 mx-auto px-4"><div className="flex gap-2 my-2 overflow-x-auto scrollbar-hide">{navLinks.map(t6 => {
            const {
              href,
              label
            } = t6;
            const isActive = decodeURIComponent(pathname) === decodeURIComponent(href);
            return <Link key={href} href={href} prefetch={false} className={cn("px-5 py-3 font-medium text-xs uppercase whitespace-nowrap", isActive ? "btn-primary" : "link-themed")}>{label}</Link>;
          })}</div></div></nav>;
    $[24] = navLinks;
    $[25] = pathname;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== t4 || $[28] !== t5) {
    t6 = <div>{t4}{t5}</div>;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  return t6;
}